import React, { useEffect, useState } from "react";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import TextInput from "../../components/UI/TextInput";
import Table from "../../components/UI/Table";
import { Button } from "../../components/UI/Button";
import { LucidePlus, ChevronUp, ChevronDown, Edit, Trash2 } from "lucide-react";
import Modal from "../../components/UI/Modal";
import Switch from "../../components/UI/Switch";
import { useAuthStore } from "../../stores/auth-store";
import { contentUseCase } from "../../../di/container";
import type { Popup } from "../../../domain/types/content";

const PopupManagement: React.FC = () => {
  const { user } = useAuthStore();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [loadedData, setLoadedData] = useState<Popup[]>([]);
  const [loading, setLoading] = useState(false);

  // 추가 모달 상태
  const [isAddPopupModalVisible, setIsAddPopupModalVisible] = useState(false);
  const [addPopupName, setAddPopupName] = useState("");
  const [addPopupFile, setAddPopupFile] = useState<File | null>(null);
  const [addPreviewUrl, setAddPreviewUrl] = useState<string | null>(null);

  // 수정 모달 상태
  const [editPopupId, setEditPopupId] = useState<number | null>(null);
  const [isEditPopupModalVisible, setIsEditPopupModalVisible] = useState(false);
  const [editPopupName, setEditPopupName] = useState("");
  const [editPopupFile, setEditPopupFile] = useState<File | null>(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState<string | null>(null);

  const loadData = async () => {
    if (!user?.companyId) return;
    setLoading(true);
    try {
      const data = await contentUseCase.getPopups(user.companyId);
      setLoadedData(data);
    } catch (error) {
      alert("팝업 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.companyId) {
      loadData();
    }
  }, [user?.companyId]);

  const resetAddModal = () => {
    setAddPopupName("");
    setAddPopupFile(null);
    setAddPreviewUrl(null);
  };

  const resetEditModal = () => {
    setEditPopupId(null);
    setEditPopupName("");
    setEditPopupFile(null);
    setEditPreviewUrl(null);
  };

  const handleCreate = async () => {
    if (!user?.companyId) return;
    if (addPopupName.trim() === "") {
      alert("팝업 이름을 입력해주세요.");
      return;
    }

    try {
      await contentUseCase.createPopup(
        user.companyId,
        addPopupName,
        false,
        addPopupFile || undefined
      );
      await loadData();
      setIsAddPopupModalVisible(false);
      resetAddModal();
    } catch (error) {
      alert("팝업 생성에 실패했습니다.");
    }
  };

  const handleUpdate = async () => {
    if (!user?.companyId || editPopupId === null) return;
    if (editPopupName.trim() === "") {
      alert("팝업 이름을 입력해주세요.");
      return;
    }

    const existing = loadedData.find((p) => p.id === editPopupId);
    if (!existing) return;

    try {
      await contentUseCase.updatePopup(
        user.companyId,
        editPopupId,
        editPopupName,
        existing.isEnabled,
        editPopupFile || undefined
      );
      await loadData();
      setIsEditPopupModalVisible(false);
      resetEditModal();
    } catch (error) {
      alert("팝업 수정에 실패했습니다.");
    }
  };

  const handleToggleEnabled = async (popup: Popup) => {
    if (!user?.companyId) return;
    try {
      await contentUseCase.updatePopup(
        user.companyId,
        popup.id,
        popup.name,
        !popup.isEnabled
      );
      await loadData();
    } catch (error) {
      alert("사용여부 변경에 실패했습니다.");
    }
  };

  const handleDelete = async (popupId: number) => {
    if (!user?.companyId) return;
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      await contentUseCase.deletePopup(user.companyId, popupId);
      await loadData();
    } catch (error) {
      alert("팝업 삭제에 실패했습니다.");
    }
  };

  const handleMoveOrder = async (index: number, direction: "up" | "down") => {
    if (!user?.companyId) return;
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= loadedData.length) return;

    const newData = [...loadedData];
    [newData[index], newData[targetIndex]] = [newData[targetIndex], newData[index]];

    setLoadedData(newData);

    try {
      await contentUseCase.reorderPopups(user.companyId, {
        popupIds: newData.map((p) => p.id),
      });
    } catch (error) {
      alert("순서 변경에 실패했습니다.");
      await loadData();
    }
  };

  return (
    <>
      {isEditPopupModalVisible && (
        <Modal setIsModalVisible={setIsEditPopupModalVisible} title="팝업 수정">
          <>
            <div className="w-[calc(100vw-20vw)] max-w-[31.875rem] mb-4" />

            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                팝업 이름
              </label>
              <TextInput
                value={editPopupName}
                onChange={(e) => setEditPopupName(e.target.value)}
                placeholder="팝업 이름"
                className="bg-gray-50"
              />
            </div>

            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                팝업 이미지
              </label>

              <div className="w-[12.5rem]">
                <label>
                  <div
                    className="
                rounded-[0.5rem]
                overflow-hidden
                w-[12.5rem]
                h-[12.5rem]
                bg-gray-50
                hover:bg-[#e6e6e6]
                transition-all
                relative
              "
                  >
                    {editPreviewUrl && (
                      <img
                        src={editPreviewUrl}
                        alt="팝업 이미지 미리보기"
                        className="object-contain w-full h-full"
                      />
                    )}
                  </div>

                  <input
                    type="file"
                    className="hidden"
                    accept=".png,.jpg,.jpeg"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setEditPreviewUrl(URL.createObjectURL(file));
                      setEditPopupFile(file);
                    }}
                  />
                </label>
              </div>
            </div>

            <Button
              onClick={handleUpdate}
              className="w-full h-[45px]"
            >
              제출
            </Button>
          </>
        </Modal>
      )}

      {isAddPopupModalVisible && (
        <Modal setIsModalVisible={setIsAddPopupModalVisible} title="팝업 추가">
          <>
            <div className="w-[calc(100vw-20vw)] max-w-[31.875rem] mb-4" />

            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                팝업 이름
              </label>
              <TextInput
                value={addPopupName}
                onChange={(e) => setAddPopupName(e.target.value)}
                placeholder="팝업 이름"
                className="bg-gray-50"
              />
            </div>

            <div className="text-start mb-[1.0625rem]">
              <label className="block text-sm font-medium mb-2">
                팝업 이미지
              </label>

              <div className="w-[12.5rem]">
                <label>
                  <div
                    className="border
                rounded-[0.5rem]
                overflow-hidden
                w-[12.5rem]
                h-[12.5rem]
                bg-gray-50
                hover:bg-[#e6e6e6]
                transition-all
                relative
              "
                  >
                    {addPreviewUrl && (
                      <img
                        src={addPreviewUrl}
                        alt="팝업 이미지 미리보기"
                        className="object-contain w-full h-full"
                      />
                    )}
                  </div>

                  <input
                    type="file"
                    className="hidden"
                    accept=".png,.jpg,.jpeg"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setAddPreviewUrl(URL.createObjectURL(file));
                      setAddPopupFile(file);
                    }}
                  />
                </label>
              </div>
            </div>

            <Button
              onClick={handleCreate}
              className="w-full h-[45px]"
            >
              제출
            </Button>
          </>
        </Modal>
      )}

      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] / flex flex-row justify-between gap-[0.5rem]">
            <h2 className="text-[1.375rem] / font-semibold">팝업 관리</h2>
            <Button onClick={() => {
              resetAddModal();
              setIsAddPopupModalVisible(true);
            }}>
              <LucidePlus className="scale-[0.9]" />
              추가하기
            </Button>
          </div>

          <div
            className="mb-[1.25rem] / p-[1.25rem]
          / border border-[#d9d9d9] / rounded-[0.625rem] / bg-white
          / flex flex-col md:flex-row gap-[0.75rem] / justify-stretch"
          >
            <TextInput
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-full"
              placeholder="팝업 이름으로 검색"
            />
          </div>
          <div className="text-[12px]">
            <Table
              tabledata={{
                head: [
                  <>순서</>,
                  <>팝업 이름</>,
                  <>팝업 이미지</>,
                  <>등록일</>,
                  <>사용 여부</>,
                  <></>,
                ],
                body: loadedData
                  .filter((item) => {
                    if (searchKeyword === "") return true;
                    return item.name
                      .toLowerCase()
                      .includes(searchKeyword.toLowerCase());
                  })
                  .map((item, index) => [
                    <div className="flex items-center gap-1">
                      <button
                        className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
                        disabled={index === 0}
                        onClick={() => handleMoveOrder(index, "up")}
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        className="p-0.5 hover:bg-gray-100 rounded disabled:opacity-30"
                        disabled={index === loadedData.length - 1}
                        onClick={() => handleMoveOrder(index, "down")}
                      >
                        <ChevronDown size={16} />
                      </button>
                    </div>,
                    <>{item.name}</>,
                    <img
                      src={item.imageUrl || ""}
                      alt="thumb"
                      className="w-12 h-12 object-cover rounded"
                    />,
                    <>{new Date(item.createdAt).toLocaleDateString()}</>,
                    <div onClick={() => handleToggleEnabled(item)} className="cursor-pointer">
                      <Switch isEnabled={item.isEnabled} />
                    </div>,
                    <div className="flex flex-row gap-[6px]">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditPopupId(item.id);
                          setEditPopupName(item.name);
                          setEditPreviewUrl(item.imageUrl);
                          setEditPopupFile(null);
                          setIsEditPopupModalVisible(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                        수정
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                        삭제
                      </Button>
                    </div>,
                  ]),
              }}
            />
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default PopupManagement;